/**
 * SuscripcionesPage — "Cuotas" del portal.
 *
 * Vive bajo PortalLayout (sin layout viejo). Compone:
 *  - PageHeader.
 *  - Card "Tarjeta inscrita" (con CTA Transbank inscription si no hay).
 *  - Card "Próximo Cobro" (siguiente nextBillingDate de subs activas).
 *  - RESUMEN: 4 StatCards (Vencidas, Próximas, Pagadas, Gratis).
 *  - Chips filtro por estado.
 *  - Lista de cuotas (payment_attempts).
 *  - Diálogos de cancelar/reactivar suscripción para gestión completa.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { PageHeader } from '@app/components/common/PageHeader';
import { EmptyState } from '@app/components/common/EmptyState';
import { StatCard } from '@app/components/common/StatCard';
import { StatusBadge, type StatusVariant } from '@app/components/common/StatusBadge';
import {
  usePaymentHistory,
  usePaymentMethods,
  useStartTransbankInscription,
  useSetPrimaryPaymentMethod,
  useDeletePaymentMethod,
  useSubscriptions,
  useCancelSubscription,
  useReactivateSubscription,
} from '@modules/billing/presentation/hooks/useBilling';
import { usePetsList } from '@modules/pets/presentation/hooks/usePets';
import type { SubscriptionResponse } from '@modules/billing/application/dto/BillingDTO';
import { formatCLP, formatDate } from '@shared/utils/formatters';
import { getErrorMessage } from '@shared/infrastructure/http/api.error';
import { cn } from '@shared/utils/cn';
import {
  CreditCard,
  Calendar,
  ChevronRight,
  Plus,
  Trash2,
  Star,
  Receipt,
} from 'lucide-react';

type CuotaFilter = 'all' | 'paid' | 'upcoming' | 'overdue' | 'free';

const FILTERS: { key: CuotaFilter; label: string }[] = [
  { key: 'all', label: 'Todas' },
  { key: 'paid', label: 'Pagadas' },
  { key: 'upcoming', label: 'Próximas' },
  { key: 'overdue', label: 'Vencidas' },
  { key: 'free', label: 'Gratis' },
];

function paymentVariant(status: string): { variant: StatusVariant; label: string } {
  const v = status.toUpperCase();
  if (v === 'SUCCESS' || v === 'PAID') return { variant: 'paid', label: 'Pagada' };
  if (v === 'FAILED') return { variant: 'overdue', label: 'Vencida' };
  if (v === 'PENDING' || v === 'IN_PROGRESS') return { variant: 'upcoming', label: 'En proceso' };
  return { variant: 'neutral', label: status };
}

export function SuscripcionesPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<CuotaFilter>('all');
  const { data: methods = [], isLoading: loadingPM } = usePaymentMethods();
  const { data: payments = [], isLoading: loadingP } = usePaymentHistory();
  const { data: subscriptions = [], isLoading: loadingSubs } = useSubscriptions();
  const { data: pets = [] } = usePetsList();
  const startInscription = useStartTransbankInscription();
  const setPrimary = useSetPrimaryPaymentMethod();
  const deletePM = useDeletePaymentMethod();
  const cancelSub = useCancelSubscription();
  const reactivateSub = useReactivateSubscription();

  const [cancelTarget, setCancelTarget] = useState<SubscriptionResponse | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [reactivateTarget, setReactivateTarget] = useState<SubscriptionResponse | null>(null);

  const primaryPM = methods.find((m) => m.isPrimary) ?? methods[0] ?? null;

  // Suscripciones activas y próxima fecha de cobro
  const activeSubs = useMemo(
    () => subscriptions.filter((s) => ['ACTIVE', 'PAST_DUE', 'PENDING'].includes((s.status ?? '').toUpperCase())),
    [subscriptions],
  );
  const nextBillingISO = useMemo(() => {
    const dates = activeSubs
      .map((s) => new Date(s.nextBillingDate))
      .filter((d) => !Number.isNaN(d.getTime()))
      .sort((a, b) => a.getTime() - b.getTime());
    return dates[0]?.toISOString();
  }, [activeSubs]);
  const nextBillingAmountClp = useMemo(() => {
    const UF_APPROX = 37000;
    return Math.round(activeSubs.reduce((acc, s) => acc + (s.finalPriceUf ?? 0), 0) * UF_APPROX);
  }, [activeSubs]);

  // Stats
  const stats = useMemo(() => {
    const paid = payments.filter((p) => p.status.toUpperCase() === 'SUCCESS').length;
    const overdue = payments.filter((p) => p.status.toUpperCase() === 'FAILED').length;
    const upcoming = activeSubs.length;
    const free = 0;
    return { paid, overdue, upcoming, free };
  }, [payments, activeSubs]);

  // Filtrado
  const filteredPayments = useMemo(() => {
    if (filter === 'all') return payments;
    if (filter === 'paid') return payments.filter((p) => p.status.toUpperCase() === 'SUCCESS');
    if (filter === 'overdue') return payments.filter((p) => p.status.toUpperCase() === 'FAILED');
    if (filter === 'upcoming') return payments.filter((p) => p.status.toUpperCase() === 'PENDING');
    return [];
  }, [payments, filter]);

  const handleStartInscription = async () => {
    try {
      const res = await startInscription.mutateAsync();
      // Transbank requiere submit POST con TBK_TOKEN al redirectUrl
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = res.redirectUrl;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'TBK_TOKEN';
      input.value = res.token;
      form.appendChild(input);
      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleSetPrimary = async (id: string) => {
    try {
      await setPrimary.mutateAsync(id);
      toast.success('Tarjeta marcada como principal');
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleDeletePM = async (id: string) => {
    if (!confirm('¿Eliminar este método de pago?')) return;
    try {
      await deletePM.mutateAsync(id);
      toast.success('Método de pago eliminado');
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleCancelSub = async () => {
    if (!cancelTarget) return;
    try {
      await cancelSub.mutateAsync({ id: cancelTarget.id, reason: cancelReason.trim() || undefined });
      toast.success('Suscripción cancelada. Cobertura vigente hasta el próximo cobro.');
      setCancelTarget(null);
      setCancelReason('');
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleReactivateSub = async () => {
    if (!reactivateTarget) return;
    try {
      await reactivateSub.mutateAsync(reactivateTarget.id);
      toast.success('Plan reactivado');
      setReactivateTarget(null);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Mis Cuotas" subtitle="Gestiona y visualiza el estado de tus pagos" />

      {/* Tarjeta inscrita */}
      <section className="rounded-2xl border border-border bg-card p-5 shadow-xs sm:p-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-muted text-muted-foreground">
              <CreditCard className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
            </span>
            <div>
              {loadingPM ? (
                <Skeleton className="h-5 w-44" />
              ) : primaryPM ? (
                <>
                  <p className="text-sm font-semibold text-foreground">
                    {primaryPM.cardBrand ?? 'Tarjeta'} •••• {primaryPM.last4 ?? primaryPM.lastFour ?? '****'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {primaryPM.expMonth && primaryPM.expYear
                      ? `Vence ${String(primaryPM.expMonth).padStart(2, '0')}/${primaryPM.expYear}`
                      : 'Tarjeta principal'}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm font-semibold text-foreground">Sin tarjeta inscrita</p>
                  <p className="text-xs text-muted-foreground">
                    Inscribe tu tarjeta para pagar tus cuotas automáticamente.
                  </p>
                </>
              )}
            </div>
          </div>
          <Button
            className="rounded-full"
            onClick={handleStartInscription}
            disabled={startInscription.isPending}
          >
            {startInscription.isPending
              ? 'Redirigiendo…'
              : primaryPM
              ? 'Cambiar tarjeta'
              : 'Inscribir tarjeta'}
          </Button>
        </div>

        {/* Listado de tarjetas (cuando hay más de una) */}
        {methods.length > 1 && (
          <ul className="mt-4 space-y-2 border-t border-border pt-4">
            {methods.map((pm) => (
              <li
                key={pm.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-muted/40 px-3 py-2"
              >
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium text-foreground">
                    {pm.cardBrand ?? 'Tarjeta'} •••• {pm.last4 ?? pm.lastFour ?? '****'}
                  </span>
                  {pm.isPrimary && (
                    <span className="rounded-full bg-primary/15 px-2 py-0.5 text-xs font-medium text-primary">
                      Principal
                    </span>
                  )}
                </div>
                <div className="flex gap-1.5">
                  {!pm.isPrimary && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSetPrimary(pm.id)}
                      disabled={setPrimary.isPending}
                      className="h-8"
                    >
                      <Star className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                      Principal
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeletePM(pm.id)}
                    disabled={deletePM.isPending}
                    className="h-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Próximo Cobro */}
      <section className="flex items-center justify-between gap-4 rounded-2xl border border-border bg-accent/40 p-5 shadow-xs sm:p-6">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-sidebar-primary text-sidebar-primary-foreground">
            <Calendar className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
          </span>
          <div>
            <p className="text-sm font-semibold text-foreground">Próximo Cobro</p>
            <p className="text-xs text-muted-foreground">
              {loadingSubs ? (
                'Cargando…'
              ) : nextBillingISO ? (
                <>
                  {formatDate(nextBillingISO)} — {formatCLP(nextBillingAmountClp)}
                </>
              ) : (
                'No hay cuotas pendientes'
              )}
            </p>
          </div>
        </div>
        {nextBillingISO && (
          <ChevronRight className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
        )}
      </section>

      {/* RESUMEN */}
      <section className="rounded-2xl border border-border bg-card p-5 shadow-xs sm:p-6">
        <p className="mb-4 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Resumen
        </p>
        <div className="grid grid-cols-2 gap-y-4 sm:grid-cols-4 sm:gap-y-0 sm:divide-x sm:divide-border">
          <StatCard label="Vencidas" value={stats.overdue} tone="destructive" />
          <StatCard label="Próximas" value={stats.upcoming} tone="sidebar" />
          <StatCard label="Pagadas" value={stats.paid} tone="success" />
          <StatCard label="Gratis" value={stats.free} tone="free" />
        </div>
      </section>

      {/* Filtrar por estado */}
      <section className="space-y-3">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Filtrar por estado
        </p>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map(({ key, label }) => (
            <button
              type="button"
              key={key}
              onClick={() => setFilter(key)}
              className={cn(
                'rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors',
                filter === key
                  ? 'border-sidebar-primary/30 bg-sidebar-primary/15 text-sidebar-primary'
                  : 'border-border bg-card text-muted-foreground hover:text-foreground',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </section>

      {/* Lista de cuotas */}
      {loadingP ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-xl" />
          ))}
        </div>
      ) : filteredPayments.length === 0 ? (
        <EmptyState
          icon={Receipt}
          title="No hay cuotas disponibles"
          description={
            filter === 'all'
              ? 'Cuando se generen cuotas asociadas a tus suscripciones, aparecerán acá.'
              : 'No hay cuotas con ese estado por ahora.'
          }
        />
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card shadow-xs">
          {filteredPayments.map((p) => {
            const sub = subscriptions.find((s) => s.id === p.subscriptionId);
            const pet = sub ? pets.find((pt) => pt.id === sub.petId) : undefined;
            const { variant, label } = paymentVariant(p.status);
            const when = p.succeededAt ?? p.failedAt ?? p.attemptedAt ?? p.createdAt;
            return (
              <li
                key={p.id}
                className="flex items-center justify-between gap-4 px-4 py-3 transition-colors hover:bg-muted/40"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-foreground">{formatDate(when)}</span>
                    <StatusBadge variant={variant}>{label}</StatusBadge>
                  </div>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">
                    {pet?.name ? `${pet.name} · ` : ''}
                    {sub?.planName ?? 'Plan'}
                    {p.errorMessage ? ` · ${p.errorMessage}` : ''}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-sm font-semibold tabular-nums text-foreground">
                    {formatCLP(p.amountClp)}
                  </p>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    {p.amountUf?.toFixed(2)} UF
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Suscripciones activas — gestión */}
      {!loadingSubs && subscriptions.length > 0 && (
        <section className="space-y-3">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Suscripciones
          </p>
          <ul className="space-y-2">
            {subscriptions.map((s) => {
              const pet = pets.find((p) => p.id === s.petId);
              const st = (s.status ?? '').toUpperCase();
              const isOpen = ['ACTIVE', 'PAST_DUE', 'PENDING'].includes(st);
              const isReactivable = ['CANCELED', 'CANCELLED', 'SUSPENDED'].includes(st);
              return (
                <li
                  key={s.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-card p-4 shadow-xs"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground">
                      {pet?.name ?? 'Mascota'} · {s.planName ?? 'Plan'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {s.finalPriceUf.toFixed(2)} UF/mes · próximo cobro {formatDate(s.nextBillingDate)}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {s.planId && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full"
                        onClick={() => navigate(`/mi-cuenta/planes/${s.planId}`)}
                      >
                        Ver plan
                      </Button>
                    )}
                    {isOpen && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full border-destructive/30 text-destructive hover:bg-destructive/10"
                        onClick={() => {
                          setCancelTarget(s);
                          setCancelReason('');
                        }}
                      >
                        Cancelar
                      </Button>
                    )}
                    {isReactivable && (
                      <Button size="sm" className="rounded-full" onClick={() => setReactivateTarget(s)}>
                        Reactivar
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* Agregar mascota */}
      {activeSubs.length > 0 && (
        <Button
          variant="outline"
          className="w-full rounded-full sm:w-auto"
          onClick={() => navigate('/contratacion')}
        >
          <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
          Añadir mascota al contrato
        </Button>
      )}

      {/* Dialog Cancelar */}
      <Dialog
        open={!!cancelTarget}
        onOpenChange={(open) => {
          if (!open) {
            setCancelTarget(null);
            setCancelReason('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar suscripción</DialogTitle>
            <DialogDescription className="pt-2">
              Vas a cancelar el plan de{' '}
              <strong>{pets.find((p) => p.id === cancelTarget?.petId)?.name ?? 'tu mascota'}</strong>.
              Si ya pagaste el mes en curso, sigues cubierto hasta{' '}
              <strong>
                {cancelTarget?.nextBillingDate ? formatDate(cancelTarget.nextBillingDate) : '—'}
              </strong>
              . No se cobrará el próximo ciclo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="cancel-reason" className="text-xs">
              Motivo (opcional)
            </Label>
            <Textarea
              id="cancel-reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Nos ayuda a mejorar"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelTarget(null)}>
              Mantener
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelSub}
              disabled={cancelSub.isPending}
            >
              {cancelSub.isPending ? 'Cancelando…' : 'Sí, cancelar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Reactivar */}
      <Dialog
        open={!!reactivateTarget}
        onOpenChange={(open) => {
          if (!open) setReactivateTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reactivar plan</DialogTitle>
            <DialogDescription className="pt-2">
              Vas a reactivar el plan de{' '}
              <strong>{pets.find((p) => p.id === reactivateTarget?.petId)?.name ?? 'tu mascota'}</strong>.
              Se cobrará el primer mes contra tu tarjeta principal.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReactivateTarget(null)}>
              Cancelar
            </Button>
            <Button onClick={handleReactivateSub} disabled={reactivateSub.isPending}>
              {reactivateSub.isPending ? 'Reactivando…' : 'Reactivar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
